import * as React from 'react';
import useDevice from '@/hooks/use-device';
import QRCode from 'react-qr-code';
import { generateSwishUrl } from '@/utils/Utils';
import useGlobalStore from '@/stores/global.store';
import { useCurrentPaymentQuery } from '@/hooks/queries/use-payment-query';
import { PaymentStatus } from '@/types/api/payment';
import { useCode } from '@/hooks/use-code';
import { useLocation } from 'react-router-dom';

export interface PaymentPendingModalProps {}

export default function PaymentPendingModal({}: PaymentPendingModalProps) {
  const { data: payment } = useCurrentPaymentQuery();
  const { isMobileOrTablet } = useDevice();
  const [swishOnThisDevice, setSwishOnThisDevice] = React.useState<boolean>(isMobileOrTablet);
  const ActiveComponent = swishOnThisDevice ? ThisDeviceContent : NotThisDeviceContent;

  return (
    <>
      <div className="mb-4 text-center">
        <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 mt-4">Inväntar betalning...</div>
      </div>

      {/* Modal content */}
      <div className="text-center">
        <div className="flex flex-col items-center">
          {payment?.status === PaymentStatus.Initiating && (
            <div className="inline-flex my-10">
              <div
                aria-hidden
                role="status"
                aria-live="polite"
                aria-busy="true"
                className="size-16 animate-spin rounded-full border-8 border-gray-200 border-t-violet-500"
              />
            </div>
          )}
          {payment?.status === PaymentStatus.Pending && (
            <ActiveComponent swishToken={payment?.methodData.swishToken!} setSwishOnThisDevice={setSwishOnThisDevice} />
          )}
        </div>
      </div>
    </>
  );
}

interface ContentProps {
  swishToken: string;
  setSwishOnThisDevice: (value: boolean) => void;
}

function ThisDeviceContent({ swishToken, setSwishOnThisDevice }: ContentProps) {
  const paymentId = useGlobalStore((state) => state.paymentId);
  const orderId = useGlobalStore((state) => state.orderId);
  const code = useCode();
  const location = useLocation();
  // Set by PaymentCallback. If the payment is still pending when the user comes back from Swish,
  // reopening the app would bounce them straight back into it, so leave that to the button.
  // Scoped to that payment, so a retry with a new payment opens Swish as usual.
  const returnedFromSwish = location.state?.returnedFromPaymentId === paymentId;

  const swishUrl = generateSwishUrl(swishToken, paymentId!, orderId!, code);

  React.useEffect(() => {
    if (returnedFromSwish) return;

    window.location = swishUrl as any;
  }, [swishUrl, returnedFromSwish]);

  return (
    <>
      <a
        href={swishUrl}
        className="btn-lg mt-4 mb-8 bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
      >
        Öppna Swish-appen
      </a>
      <div className="text-sm">Klicka på knappen och slutför betalning.</div>
      <div className="text-sm mb-5">
        Swish på annan enhet?{' '}
        <button
          onClick={() => setSwishOnThisDevice(false)}
          type="button"
          className="underline text-violet-500 hover:text-violet-800"
        >
          Visa QR-kod istället.
        </button>
      </div>
    </>
  );
}

function NotThisDeviceContent({ swishToken, setSwishOnThisDevice }: ContentProps) {
  return (
    <>
      <QRCode size={200} value={`D${swishToken}`} className="mt-4 mb-8" />
      <div className="text-sm">Öppna Swish-appen på din mobil och scanna QR-koden.</div>
      <div className="text-sm mb-5">
        <button
          onClick={() => setSwishOnThisDevice(true)}
          type="button"
          className="underline text-violet-500 hover:text-violet-800"
        >
          Swish på denna enhet?
        </button>
      </div>
    </>
  );
}
